import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { ArrowLeft, Check, Image as ImageIcon, Search } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
} from '@artstorefronts/ui';
import { waitingFlowVersionId } from '@arthelper/shared/acquisition/strategy';
import AdminLayout from '../../../components/AdminLayout';
import {
  ACQUISITION_STRATEGY,
  HELD_MEMBER_IDS,
  HELD_MEMBERS,
  LET_IN_MEMBERS,
  type HeldMember,
  type HeldPage,
  type HeldWizard,
  type Strategy,
} from './queries';
import { errorMessage, formatDate, label } from './shared';

const PAGE_SIZE = 50;
const SEARCH_DELAY_MS = 300;

const WIZARD_LABELS: Record<HeldWizard, { text: string; className: string }> = {
  completed: { text: 'Profile finished', className: 'bg-emerald-50 text-emerald-800 ring-emerald-200' },
  in_progress: { text: 'Profile started', className: 'bg-amber-50 text-amber-800 ring-amber-200' },
  not_started: { text: 'Not started', className: 'bg-gray-50 text-gray-600 ring-gray-200' },
  none: { text: 'No wizard', className: 'bg-gray-50 text-gray-500 ring-gray-200' },
};

const FILTERS: Array<{ value: HeldWizard | null; label: string }> = [
  { value: null, label: 'Everyone' },
  { value: 'completed', label: 'Finished' },
  { value: 'in_progress', label: 'Started' },
  { value: 'not_started', label: 'Not started' },
];

function people(count: number): string {
  return `${count.toLocaleString()} ${count === 1 ? 'person' : 'people'}`;
}

function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/**
 * Who is waiting under one strategy, on a page of its own: there can be thousands. Search by
 * name or email, filter by how far they got with their profile, pick people (or every match) and
 * let them in. Finished profiles come first. Nobody is ever told no: whoever isn't picked keeps
 * waiting, and a friend's signup still lets them in.
 */
function WaitingToGetIn() {
  const { id = '' } = useParams<{ id: string }>();
  const [query, setQuery] = useState('');
  const search = useDebounced(query.trim(), SEARCH_DELAY_MS);
  const [wizard, setWizard] = useState<HeldWizard | null>(null);
  const [offset, setOffset] = useState(0);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [confirming, setConfirming] = useState(false);

  useEffect(() => setOffset(0), [search, wizard]);

  const { data: strategyData } = useQuery<{ acquisitionStrategyById: Strategy | null }>(ACQUISITION_STRATEGY, {
    variables: { id },
  });
  const strategy = strategyData?.acquisitionStrategyById ?? null;
  const hasWizard = strategy ? waitingFlowVersionId(strategy.settings) !== null : true;

  const variables = { strategyId: id, search: search || null, wizard, limit: PAGE_SIZE, offset };
  const { data, loading, refetch } = useQuery<{ acquisitionHeldMembers: HeldPage }>(HELD_MEMBERS, {
    variables,
    fetchPolicy: 'cache-and-network',
  });
  const [loadIds, { loading: loadingIds }] = useLazyQuery<{ acquisitionHeldMemberIds: string[] }>(HELD_MEMBER_IDS, {
    fetchPolicy: 'network-only',
  });
  const [letIn, { loading: admitting }] = useMutation<{ letInAcquisitionMembers: number }>(LET_IN_MEMBERS);

  const page = data?.acquisitionHeldMembers;
  const members = page?.members ?? [];
  const matching = page?.matching ?? 0;

  const toggle = (userId: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const pickAllMatching = async () => {
    const result = await loadIds({ variables: { strategyId: id, search: search || null, wizard } });
    const ids = result.data?.acquisitionHeldMemberIds ?? [];
    setPicked((prev) => new Set([...prev, ...ids]));
  };

  const admit = async () => {
    try {
      const result = await letIn({ variables: { userIds: Array.from(picked) } });
      toast.success(`Let in ${people(result.data?.letInAcquisitionMembers ?? 0)}.`);
      setPicked(new Set());
      await refetch();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not let them in.'));
    }
  };

  const filters = hasWizard ? FILTERS : FILTERS.slice(0, 1);
  const first = matching === 0 ? 0 : offset + 1;
  const last = Math.min(offset + PAGE_SIZE, matching);

  return (
    <AdminLayout
      title="Waiting to get in"
      description={
        page
          ? `${strategy ? `${label(strategy)} · ` : ''}${people(page.total)} waiting · ${page.finished.toLocaleString()} finished their profile. Finished profiles first. Nobody is told no: whoever you don't pick keeps waiting.`
          : 'Loading…'
      }
    >
      <div className="space-y-4 px-4 md:px-0">
        <Link
          to={`/admin/acquisition/${id}`}
          className="inline-flex min-h-11 items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to the strategy
        </Link>

        {/* Sticky, so the picked count and Let in stay in reach while scrolling a long list. */}
        <section className="sticky top-0 z-30 space-y-3 rounded-lg bg-white p-4 shadow">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <Input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by name or email"
              aria-label="Search waiting members"
              className="h-11 pl-9 text-base md:text-sm"
              data-testid="held-search"
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Filter by profile progress">
              {filters.map((filter) => {
                const active = filter.value === wizard;
                return (
                  <button
                    key={filter.label}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setWizard(filter.value)}
                    className={`min-h-9 rounded-full px-3 text-sm font-medium ring-1 ${
                      active
                        ? 'bg-gray-900 text-white ring-gray-900'
                        : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {filter.label}
                    {filter.value === 'completed' && page ? ` · ${page.finished.toLocaleString()}` : ''}
                  </button>
                );
              })}
            </div>
            {matching > 0 && (
              <Button
                type="button"
                variant="outline"
                className="h-11 md:h-9"
                disabled={loadingIds}
                onClick={() => void pickAllMatching()}
              >
                {loadingIds ? 'Picking…' : `Pick all ${matching.toLocaleString()} shown`}
              </Button>
            )}
          </div>
          {picked.size > 0 && (
            <div
              className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-gray-900 px-3 py-2 text-white"
              data-testid="held-picked"
            >
              <span className="text-sm font-medium">{people(picked.size)} picked</span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  className="h-11 text-white hover:bg-white/10 hover:text-white md:h-9"
                  onClick={() => setPicked(new Set())}
                >
                  Clear
                </Button>
                <Button
                  type="button"
                  className="h-11 bg-white text-gray-900 hover:bg-gray-100 md:h-9"
                  disabled={admitting}
                  onClick={() => setConfirming(true)}
                >
                  Let in {people(picked.size)}
                </Button>
              </div>
            </div>
          )}
        </section>

        <section className="rounded-lg bg-white shadow" data-testid="held-members">
          {loading && members.length === 0 && <p className="p-4 text-sm text-gray-500">Loading…</p>}
          {!loading && members.length === 0 && (
            <p className="p-4 text-sm text-gray-500">
              {search || wizard ? 'Nobody waiting matches that.' : 'Nobody is waiting.'}
            </p>
          )}
          <ul className="divide-y divide-gray-100">
            {members.map((member) => (
              <HeldRow
                key={member.userId}
                member={member}
                checked={picked.has(member.userId)}
                onToggle={() => toggle(member.userId)}
              />
            ))}
          </ul>
          {matching > PAGE_SIZE && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 p-3 text-sm text-gray-600">
              <span>
                {first.toLocaleString()}–{last.toLocaleString()} of {matching.toLocaleString()}
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 md:h-9"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 md:h-9"
                  disabled={last >= matching}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </section>
      </div>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Let {people(picked.size)} in?</AlertDialogTitle>
            <AlertDialogDescription>
              They get access right away and an email saying they&apos;re in. Their profile and work go public, and they
              pick up the new-member wizard where they left off. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11 md:h-9">Cancel</AlertDialogCancel>
            <AlertDialogAction className="h-11 md:h-9" onClick={() => void admit()}>
              Let in
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}

function HeldRow({ member, checked, onToggle }: { member: HeldMember; checked: boolean; onToggle: () => void }) {
  const status = WIZARD_LABELS[member.wizard];
  return (
    <li>
      <label className="flex cursor-pointer items-start gap-3 p-4 hover:bg-gray-50">
        <input
          type="checkbox"
          className="mt-1 h-5 w-5 shrink-0"
          checked={checked}
          onChange={onToggle}
          aria-label={`Pick ${member.name}`}
        />
        {member.avatarUrl ? (
          <img src={member.avatarUrl} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
        ) : (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-500">
            {member.name.slice(0, 1).toUpperCase()}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium text-gray-900">{member.name}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs ring-1 ${status.className}`}>
              {member.wizard === 'completed' && <Check className="mr-1 inline h-3 w-3" aria-hidden />}
              {status.text}
            </span>
          </span>
          <span className="block truncate text-xs text-gray-500">
            {member.email} · waiting since {formatDate(member.waitingSince)} · {member.pieces}{' '}
            {member.pieces === 1 ? 'piece' : 'pieces'} · {member.friendsJoined}{' '}
            {member.friendsJoined === 1 ? 'friend' : 'friends'} joined
          </span>
          {member.bio && <span className="mt-1 line-clamp-2 block text-sm text-gray-700">{member.bio}</span>}
          {member.thumbnails.length > 0 ? (
            <span className="mt-2 flex gap-1.5">
              {member.thumbnails.map((url) => (
                <img key={url} src={url} alt="" className="h-14 w-14 rounded object-cover" />
              ))}
            </span>
          ) : (
            member.wizard !== 'none' && (
              <span className="mt-1 flex items-center gap-1 text-xs text-gray-400">
                <ImageIcon className="h-3 w-3" aria-hidden />
                {member.pieces > 0 ? 'Work still processing' : 'No work uploaded yet'}
              </span>
            )
          )}
        </span>
      </label>
    </li>
  );
}

export default WaitingToGetIn;
